<template>
  <MainLayout content-class="v-cash-content">
    <LayoutContentHeader class="v-cash-content__header v-cash-content-header">
      <ElDatePicker
        class="v-cash-content-header__date"
        v-model="date"
        type="daterange"
        unlink-panels
        format="DD.MM.YY"
        value-format="DD.MM.YY"
        :start-placeholder="$t('DateAndTime.StartDate')"
        :end-placeholder="$t('DateAndTime.EndDate')" />

      <SelectInvoiceStatus class="v-cash-content-header__status" clearable v-model="status.value" />

      <UiSelect
        :options="statusOptions"
        v-model="statusSelect.value"
        :placeholder="$t('CashRegister.PaymentType')" />

      <PatientsSearchSelect
        class="v-cash-content-header__user"
        :model-value="patient.value.id"
        :default-item="patientFromRoute"
        :placeholder="$t('Patients.SelectPatient')"
        clearable
        only-icon
        @select="handleSelectPatient" />

      <UiModelsAutocompleteSearch
        v-if="!isUserDoctor"
        class="v-cash-content-header__doctor"
        :model-value="doctor.value.id"
        :model-for-use="Doctor"
        :default-item="doctorFromRoute"
        :placeholder="$t('Appointments.SelectDoctor')"
        clearable
        @select="handleSelectDoctor" />

      <template #actions>
        <a :href="exportDataURL" download @click="sendStatisticDownload">
          <ElButton v-show="!isDoctor" plain text>
            <template #icon>
              <MiIcon icon="DOWNLOAD" />
            </template>
            {{ $t('Base.Download') }}
          </ElButton>
        </a>

        <ElButton type="primary" @click="createInvoice">
          <template #icon>
            <MiIcon icon="PLUS" />
          </template>
          {{ $t('Invoices.Create') }}
        </ElButton>
      </template>
    </LayoutContentHeader>

    <div class="v-cash-content__statistic">
      <UiSimpleStatisticCard
        v-for="item in statisticCards"
        :key="item.key"
        v-loading="statistic.loading"
        :label="item.label"
        :value="item.value"
        :icon="item.icon" />
    </div>

    <InvoicesTable
      class="v-cash-content__table"
      :items="items"
      :loading="loading"
      v-model:page="page.value"
      v-model:per-page="perPage.value"
      :total="total"
      @item:edit="editItem" />
  </MainLayout>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import noop from 'lodash.noop';

import { MiIcon } from '~shared/ui';
import { compareQueriesThenLoadData } from '@/utils/router.utils';
import { mergeOrCreateQuery } from '@/utils/http.util';
import { deleteEmptyValueKeys } from '@/utils/object.util';
import { formatPrice } from '@/utils/price.util';
import {
  usePage,
  usePerPage,
  useUser,
  useGetDataByInterval,
  useQuery,
  authStorageService,
  I18nService,
  DeviceService,
  amplitudeService,
} from '~shared/lib';
import { Invoice } from '@/models/Invoice.model';
import { Doctor } from '@/models/Doctor.model';
import { User } from '@/models/User.model';
import { GlobalModalClose } from '~widgets/GlobalModalAndDrawer';
import { ISOStringToDateAppFormat } from '@/utils/dateAndTime.utils';
import { CASH_GET_DATA_INTERVAL } from '~shared/config';
import { MainLayout } from '~widgets/layouts';
import LayoutContentHeader from '@/components/layouts/assets/LayoutContentHeader/index.vue';
import SelectInvoiceStatus from '@/components/invoices/SelectInvoiceStatus/index.vue';
import InvoicesTable from '@/components/invoices/InvoicesTable/index.vue';
import CreateOrEditOrPayInvoiceModal from '@/components/invoices/CreateOrEditOrPayInvoiceModal/index.vue';
import PatientsSearchSelect from '@/components/patients/PatientsSearchSelect/index.vue';
import UiSelect from '@/components/ui/UiSelect/index.vue';
import { useSessionStore } from '~entities/session';

export default {
  name: 'VCash',
  components: {
    UiSelect,
    MainLayout,
    PatientsSearchSelect,
    InvoicesTable,
    SelectInvoiceStatus,
    LayoutContentHeader,
    MiIcon,
  },
  setup: () => ({
    getDataByInterval: noop,

    perPage: usePerPage(),
    page: usePage(),
    startAt: useQuery({
      field: 'start_at',
      defaultValue: ISOStringToDateAppFormat(new Date().toISOString()),
    }),
    endAt: useQuery({
      field: 'end_at',
      defaultValue: ISOStringToDateAppFormat(new Date().toISOString()),
    }),
    status: useQuery({ field: 'status' }),
    statusSelect: useQuery({ field: 'payment_driver' }),
    patient: useUser(),
    doctor: useUser({
      fieldNames: {
        id: 'doctor_id',
        name: 'doctor_name',
      },
    }),

    Invoice,
    Doctor,
  }),
  data() {
    return {
      statistic: {
        loading: false,
        /** @type {InvoiceStatistic} data */
        data: {},
      },
      statusOptions: [],
      paymentDrivers: this.paymentDrivers || [],
      sessionStore: useSessionStore(),
    };
  },
  computed: {
    ...mapState({
      loading: (state) => state.invoices.loading,
      items: (state) => state.invoices.data,
      total: (state) => state.invoices.total,
    }),
    currentLocale() {
      return I18nService.getLocale();
    },
    user() {
      return this.sessionStore.user;
    },

    isDoctor() {
      return this.user.role === User.enum.roles.Doctor;
    },
    queryWatchers() {
      return {
        page: this.page.value,
        per_page: this.perPage.value,
        start_at: this.startAt.value,
        end_at: this.endAt.value,
        status: this.status.value,
        user_id: this.patient.value.id,
        doctor_id: this.doctor.value.id,
        payment_type: this.statusSelect.value,
      };
    },

    statisticCards() {
      const iconsByKey = {
        [Invoice.enum.StatisticKeys.Count]: 'BOARD',
        [Invoice.enum.StatisticKeys.Amount]: 'CASH_CHECK',
        [Invoice.enum.StatisticKeys.PayedAmount]: 'CASH_CHECK',
      };

      return Object.keys(Invoice.enum.StatisticKeys).map((enumKey) => {
        const key = Invoice.enum.StatisticKeys[enumKey];
        const label = this.$t(`Invoices.Statistic.${key}`);
        const value = [
          Invoice.enum.StatisticKeys.Amount,
          Invoice.enum.StatisticKeys.PayedAmount,
        ].includes(key)
          ? formatPrice({ price: this.statistic.data[key] })
          : this.statistic.data[key];
        const icon = iconsByKey[key];

        return {
          key: key,
          label: label,
          value: value,
          icon: icon,
        };
      });
    },

    date: {
      get() {
        return [this.startAt.value, this.endAt.value];
      },
      set(value) {
        this.startAt.value = value ? value[0] : null;
        setTimeout(() => (this.endAt.value = value ? value[1] : null));
      },
    },

    isUserDoctor() {
      return this.user.role === User.enum.roles.Doctor;
    },

    patientFromRoute() {
      return this.patient.value.id && this.patient.value.name ? { ...this.patient.value } : null;
    },

    doctorFromRoute() {
      return this.doctor.value.id && this.doctor.value.name ? { ...this.doctor.value } : null;
    },

    exportDataURL() {
      return mergeOrCreateQuery({
        url: Invoice.exportDataURL,
        query: deleteEmptyValueKeys({
          ...this.queryWatchers,
          lang: I18nService.getLocale(),
          token: authStorageService.getAccessToken(),
          device_id: DeviceService.getDeviceId(),
        }), // per_page, page будут игнорироваться на бэке
      });
    },
  },

  watch: {
    'currentLocale'() {
      this.statusOptions = this.statusOptions.map((payment) => {
        return {
          ...payment,
          label: this.$t(`Transactions.PaymentTypes.${payment.value}`),
        };
      });
    },
    'queryWatchers': {
      handler(value, oldValue) {
        compareQueriesThenLoadData({
          query: value,
          oldQuery: oldValue,
          resetPage: this.page.reset,
          getData: this.getDataByInterval,
          fieldsForResetPage: ['status', 'start_at', 'end_at', 'doctor_id'],
        });
      },
      deep: true,
    },
    'paymentDrivers': {
      handler(newPaymentDrivers) {
        if (newPaymentDrivers) {
          this.statusOptions = newPaymentDrivers.map((payment) => ({
            label: this.$t(`Transactions.PaymentTypes.${payment}`),
            value: payment,
          }));
        }
      },
      deep: true,
    },
    'date'(val, oldVal) {
      if (val.join() !== oldVal.join()) {
        amplitudeService.logEvent('invoices.change_time_range');
      }
    },
    'status.value'() {
      amplitudeService.logEvent('invoices.change_status');
    },
    'statusSelect.value'() {
      amplitudeService.logEvent('invoices.change_payment_type');
    },
  },

  methods: {
    ...mapActions({
      setLoading: 'invoices/setLoading',
      setData: 'invoices/setData',
      createItem: 'invoices/createItem',
      editItem: 'invoices/editItem',
    }),

    handleSelectPatient($event) {
      this.patient.value = $event;
      amplitudeService.logEvent('invoices.change_patient');
    },

    handleSelectDoctor($event) {
      this.doctor.value = $event;
      amplitudeService.logEvent('invoices.change_doctor');
    },

    sendStatisticDownload() {
      amplitudeService.logEvent('invoices.download_excel');
    },

    async getInvoicesAndStatistic() {
      this.getInvoices();
      this.getStatistic();
    },

    async getInvoices() {
      if (this.queryWatchers.start_at && !this.queryWatchers.end_at) return;
      this.setLoading(true);

      try {
        let data = {};

        if (this.isUserDoctor) {
          const response = await Invoice.getDataByDoctor(this.queryWatchers);
          data = response.data;
        } else {
          const response = await Invoice.find(this.queryWatchers);
          data = response.data;
        }

        this.setData({
          items: data.data,
          total: +data.meta.total,
          overwriteDataState: true,
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
        this.$notify({
          type: 'error',
          title: err?.response?.data?.message || this.$t('Notifications.Error'),
        });
      } finally {
        this.setLoading(false);
      }
    },

    async getStatistic() {
      this.statistic.loading = true;

      try {
        let statistic = {};

        if (this.isUserDoctor) {
          const response = await Invoice.getStatisticByDoctor(this.queryWatchers);
          statistic = response.data.data;
        } else {
          const response = await Invoice.getStatistic(this.queryWatchers);
          statistic = response.statistic;
        }

        this.statistic.data = statistic;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
        this.$notify({
          type: 'error',
          title: err?.response?.data?.message || this.$t('Notifications.Error'),
        });
      } finally {
        this.statistic.loading = false;
      }
    },

    async createInvoice() {
      amplitudeService.logEvent('open_create_invoice_modal');

      const action = await this.$store.dispatch(
        'modalAndDrawer/openModal',
        CreateOrEditOrPayInvoiceModal
      );

      if (action instanceof GlobalModalClose) return;
      this.createItem(action.data.invoice);
    },

    async getPaymentDrivers() {
      const response = await Invoice.getPaymentDrivers();
      this.paymentDrivers = response.data;
    },

    exportData() {
      Invoice.export();
    },
  },

  mounted() {
    this.getDataByInterval = useGetDataByInterval(
      this.getInvoicesAndStatistic,
      CASH_GET_DATA_INTERVAL
    );
    this.getDataByInterval();
    this.getPaymentDrivers();

    amplitudeService.logEvent('view_cash');
  },
};
</script>

<style lang="scss" src="./index.scss" />
<i18n src="./index.locales.json" />
